import Environment from '../environment';
import { hamletApi } from './hamlet-api';

export const ProductKeys = {
  CONFLUENCE: "confluence.ondemand",
  JIRA_CORE: "jira-core.ondemand",
  JIRA_BUSINESS: "jira-core.ondemand",
  JIRA_SOFTWARE: "jira-software.ondemand",
  JIRA_SERVICE_DESK: "jira-servicedesk.ondemand",
  JIRA_SERVICE_MANAGEMENT: "jira-servicedesk.ondemand",
  JIRA_WORK_MANAGEMENT: "jira-core.ondemand",
  JIRA_PRODUCT_DISCOVERY: "jira-product-discovery",
  BITBUCKET: "com.atlassian.bitbucket",
  OPSGENIE: "opsgenie",
  STATUSPAGE: "statuspage",
  TRELLO: "trello",
  ROVO: "rovo",
  COMPASS: "compass",
  LOOM: "loom",
  GUARD: "atlassian-access"
};

export const transformAvailableProductsIntoAvailableSites = (availableProductsSites) => {
  return availableProductsSites.map(site => {
    return {
      adminAccess: site.adminAccess,
      products: site.products ?? site.availableProducts.map(availableProduct => ProductKeys[availableProduct.productType]),
      cloudId: site.cloudId,
      displayName: site.displayName,
      avatarUrl: site.avatar,
      url: site.url,
    };
  });
}

export class HamletUser {
  token: null;
  initialized: boolean;
  user: any;
  sites: any[];
  performanceBoostExperiment: boolean;
  extendedTrialAvailability: any;
  organisationDetails: any;

  availableProductsData: { cached: any | null; request: Promise<any> | null } = { cached: null, request: null };
  meData: { cached: any | null; request: Promise<any> | null } = {cached: null, request: null};

  constructor() {

    this.token = null;
    this.initialized = false;

    this.user = {
      contactDetails: {
        firstName: '',
        lastName: '',
        email: ''
      },
      organisationDetails: {
        address1: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        isoCountryCode: '',
        organisationName: '',
        organisationType: null,
        payOnAccount: false,
        taxId: ''
      },
      lassoDetails: {
        firstName: '',
        lastName: '',
        username: '',
        displayName: ''
      },
      myDetails: {
        aaid: null,
        avatar: null,
        email: '',
        name: '',
        firstName: '',
        lastName: ''
      },
      isLoggedIn: false
    };

    this.sites = [];
    this.performanceBoostExperiment = false;

  }

  initUser(performanceBoostExperiment: boolean, enableAisAvailableProducts) {

    if (this.initialized) {
      return Promise.resolve(this.user);
    }
    this.initialized = true;

    return Promise.all([
      this.retrieveXsrfToken(),
      this.getStargateProfile(performanceBoostExperiment, false, enableAisAvailableProducts)
    ])
      .then(() => {
        return Promise.resolve(this.user);
      })
      .catch(() => {
        return Promise.resolve(this.user);
      });
  }

  retrieveXsrfToken() {

    const options = {
      url: `${hamletApi.getHamletPath()}/1.0/auth/xsrf/plain`,
      method: 'GET',
      withCredentials: true
    };

    return hamletApi.request(options)
      .then((response) => {

        if (response.data && response.data !== '') {

          this.setToken(response.data);
          this.user.isLoggedIn = true;

          return Promise.resolve();
        }

        this.user.isLoggedIn = false;
        return Promise.reject();
      })
      .catch(() => {

        this.user.isLoggedIn = false;
        return Promise.reject();
      });
  }

  getStargateProfile(performanceBoostExperiment: boolean, debugAllSites?: boolean) {

    if (performanceBoostExperiment) {
      this.performanceBoostExperiment = true;
    }

    return Promise.all([
      this.retrieveMe(),
      this.retrieveSitesData(debugAllSites)
    ])
      .then(([identityData, sitesData]) => {
        if (identityData && sitesData) {
          Object.assign(this.user.myDetails, identityData);
          this.setSites(sitesData);
          this.user.isLoggedIn = true;
        }

        return Promise.resolve();
      })
      .catch(() => {

        this.user.isLoggedIn = false;
        return Promise.resolve();
      });
  }

  retrieveCurrentUser(params) {
    params.url = '/1.0/public/contact/currentUser';

    return hamletApi.hamletRequest(params);
  }

  retrieveUserDetails(params) {
    params.url = '/1.0/public/auth/user/details'

    return hamletApi.hamletRequest(params);
  }

  retrieveMe() {
    const hostname = Environment.getAPIPrivateURL();
    const url = `${hostname}/me`;

    const options = {
      url: url,
      method: 'GET',
      withCredentials: true
    };


    let request;
    if (this.meData.cached) {
      // response available
      request = Promise.resolve(this.meData.cached);
    } else if (this.meData.request) {
      // pending request
      request = this.meData.request;
    } else {
      // first request
      this.meData.request = hamletApi.request(options);
      request = this.meData.request;
    }
    return request
      .then(result => {

        if (!result.data) {
          return Promise.reject();
        }

        this.meData.cached = result;
        this.meData.request = null;

        // clean up any names that are emails here
        let name = result.data.name;
        if (name.indexOf('@') > -1) {
          name = name.split('@')[0];
        }

        const stargateData = {
          aaid: (result.data.account_id),
          avatar: (result.data.picture),
          email: (result.data.email),
          name: (name),
          firstName: (this.getFirstName(name)),
          lastName: (this.getLastName(name))
        };

        return Promise.resolve(stargateData);
      })
      .catch(() => {

        this.user.isLoggedIn = false;
        return Promise.reject();
      });
  }

  retrieveSitesData(debugAllSites?: boolean) {
    return this.retrieveAvailableProductsData(debugAllSites).then(availableProductsSites => {
      const sitesData = transformAvailableProductsIntoAvailableSites(availableProductsSites);
      return sitesData;
    }).catch(() => {
      this.user.isLoggedIn = false;
    });
  }

  retrieveAvailableProductsData(debugAllSites?: boolean) {
    let url = `${Environment.getAPIPrivateURL()}/ais/available-products`;
    const options = {
      url: url,
      method: 'GET',
      withCredentials: true
    };

    let request;
    if (this.availableProductsData.cached) {
      // response available
      request = Promise.resolve(this.availableProductsData.cached);
    } else if (this.availableProductsData.request) {
      // pending request
      request = this.availableProductsData.request;
    } else {
      // first request
      this.availableProductsData.request = hamletApi.request(options);
      request = this.availableProductsData.request;
    }
    return request.then(result => {

      if (!result.data) {
        return [];
      }

      this.availableProductsData.cached = result;
      this.availableProductsData.request = null;

      const sitesResult = result.data.sites || [];
      const availableProductsSites = debugAllSites ? sitesResult : sitesResult.filter((site) => this.isValidSite(site));
      return Promise.resolve(availableProductsSites);
    }).catch(() => {
      this.user.isLoggedIn = false;
      return Promise.reject();
    });
  }

  checkExtendedTrialAvailability(migrationSourceUuid, productKey, cloudIds) {
    const options = {
      url: `${hamletApi.getHamletPath()}/1.0/public/account/${migrationSourceUuid}/extendedTrial/availability`,
      method: 'POST',
      data: {
        productKey,
        cloudIds,
      },
      withCredentials: true
    };

    return hamletApi.request(options)
      .then((response) => {
        if (response.data && response.data !== '') {
          this.setExtendedTrialAvailability(response.data);
          return Promise.resolve(response.data);
        }

        return Promise.resolve();
      })
      .catch(() => {
        return Promise.resolve();
      });
  }

  getExtendedTrialAvailability() {
    return this.extendedTrialAvailability;
  }

  setExtendedTrialAvailability(extendedTrialAvailability) {
    this.extendedTrialAvailability = extendedTrialAvailability;
  }

  setToken(token) {
    this.token = token;
  }

  getToken() {
    return this.token;
  }

  setUser(options) {
    Object.assign(this.user, options);
  }

  getUser() {
    return this.user;
  }

  setSites(sites) {
    this.sites = sites;
  }

  getSites() {
    return this.sites;
  }

  getSitesByProduct(availableProducts) {

    let groupSitesByProduct = {};

    this.sites.forEach((site) => {
      site.products.forEach((product) => {
        (groupSitesByProduct[product] = groupSitesByProduct[product] || []).push({
          cloudId: site.cloudId,
          displayName: site.displayName,
          url: site.url,
          avatarUrl: site.avatarUrl || '',
          isVertigo: site.isVertigo || false,
        })
      })
    });

    let result = [];
    for (var product in groupSitesByProduct) {

      if (product.length) {
        result.push({
          name: availableProducts[product].name,
          icon: availableProducts[product].login_logo_url || '',
          sites: groupSitesByProduct[product].map((site) => site.url)
        });
      }
    }
    return result;
  }

  getDisplayName() {
    return this.user.myDetails.name;
  }

  getFirstName(fullName = '') {
    return fullName.trim().split(' ')[0];
  }

  getLastName(fullName = '') {
    let lastName = '';
    let names = fullName.trim().split(' ');

    if (names.length > 1) {
      names.shift();
      lastName = names.join(' ');
    }

    return lastName;
  }

  getUserEmail() {
    return this.user.myDetails.email || '';
  }

  getUserAaid() {
    return this.user.myDetails.aaid || '';
  }

  getAvatar() {
    return this.user.myDetails.avatar;
  }

  isLoggedIn() {
    return this.user.isLoggedIn && this.user.myDetails.email.length > 0;
  }

  isExpert() {
    return this.organisationDetails.organisationType === 'EXPERT';
  }

  isReseller() {
    return this.organisationDetails.organisationType === 'RESELLER';
  }

  isPlatinumExpert() {
    return this.organisationDetails.organisationType === 'EXPERT' && this.organisationDetails.payOnAccount;
  }

  isValidSite(site) {

    const ignoreSiteNames = [
      'servicedog',
      'ecosystem',
      'atlassiantraining',
      'Atlassian Stride',
      'volunteerhub',
      'atlassiantraining',
      'hello-staging',
      'ecosystem',
      'riskmanagement',
      'hello-staging3',
      'hello-staging6',
      'hello-staging9',
      'jdog',
      'sre-ehlo',
      'trello',
      'Trello',
      'bitbucket',
      'Bitbucket'
    ];

    if (ignoreSiteNames.indexOf(site.displayName) > -1) {
      return false;
    }

    if (site.cloudId.startsWith('DUMMY')) {
      return false;
    }

    if (site.displayName.toUpperCase().match(".*-SANDBOX-\\d{3}$")) {
      return false
    }

    return true;
  }

}

export let hamletUser = new HamletUser();
